// LiveTradesSidebar.tsx
import { useState, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Collapse,
    Fab,
    Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Virtuoso } from 'react-virtuoso';
import { DOT_BREATHE_SX } from '../../../styles/realtimeAnimations';
import type { TradePolymarket } from '../../../types/types';
import type { MarketTrade } from '../api/useMarketTrades';
import type { SignalItem } from '../api/types';
import { TAG_COLORS } from '../api/sidebarTradeTypes';
import { Badge } from './shared';
import { TradeRow } from './TradeRow';
import { tradeKey } from './charts/tradePlate';
import { useTraderProfileCache } from '../api/useTraderProfileCache';
import { TraderStatsTooltip } from './TraderStatsTooltip';
import { formatRange } from './charts/chartFormat';
import { formatVolume } from '../utils/format';
import {
    useEventTradeSelection,
    type EventTradeSelection,
} from '../hooks/useEventTradeSelection';
import { useTradeDrain } from '../hooks/useTradeDrain';
import { useNewTradeFlash } from '../hooks/useNewTradeFlash';
import { useTraderTags } from '../hooks/useTraderTags';
import { useTradeSignalMatch } from '../hooks/useTradeSignalMatch';
import { TradeControlsPanel } from './TradeControlsPanel';
import { CONTROL_HINT_SX } from './tradeControlStyles';

interface LiveTradesSidebarProps {
    liveTrades: Map<string, TradePolymarket[]>;
    markets: {
        condition_id: string;
        question: string;
        yes_outcome?: string | null;
        outcomes?: string[] | null;
        current_yes_prob?: number | null;
    }[];
    liveProbs?: Map<string, number>;
    connected: boolean;
    historicalTrades: MarketTrade[];
    loadMoreTrades: () => void;
    loadingMore: boolean;
    hasMore: boolean;
    error?: Error | null;
    highlightedAccounts?: Set<string>;
    /** Recent trades for the selected traders, fetched per-account across the whole
     *  event, so dormant traders' freshest trades appear even when they fall outside
     *  the loaded event-wide window. Merged into the trade list. */
    selectedTraderTrades?: MarketTrade[];
    /** True while the selected traders' recent trades are still being fetched —
     *  shows a spinner instead of the empty state in Accounts mode. */
    selectedTraderTradesLoading?: boolean;
    tradesByAccount?: Map<string, MarketTrade[]>;
    accountColorMap?: Map<string, string>;
    /** Outcome chart colors — conditionId → hex color. Pass only for multi-market events. */
    outcomeColorMap?: Map<string, string>;
    onSelectMarket?: (conditionId: string) => void;
    getTraderHref?: (address: string) => string;
    getMarketHref?: (
        conditionId: string,
        hasMetadata: boolean
    ) => string | undefined;
    onToggleAccount?: (address: string) => void;
    /** Disable trader stats tooltip on hover (e.g. when already on TraderView) */
    disableTraderTooltip?: boolean;
    /** Event-wide signals for matching to individual trades */
    signals?: SignalItem[];
    /** Key of the trade hovered on the plot, for list highlighting. */
    hoveredTradeKey?: string | null;
    /** Notified when a trade row is hovered/unhovered, for plot linking. */
    onTradeHover?: (key: string | null) => void;
    /**
     * The chart's visible window. When set, the list shows only trades inside it.
     * Read-only: the sidebar reflects the window but never changes it — clearing a
     * zoom belongs to the chart, which owns the gesture that set it.
     */
    timeRange?: [Date, Date] | null;
    /**
     * Rows fetched for the current window, reset when the window changes. The
     * drain fences on this rather than on `historicalTrades.length`, which
     * carries rows over from a previous window.
     */
    loadedForWindow?: number;
}

interface LiveTradesSidebarViewProps extends LiveTradesSidebarProps {
    /** The window's trades and their controls, owned by the parent view. */
    tradeSelection: EventTradeSelection;
}

const EMPTY_ACTION_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    px: 1.25,
    border: 1,
    borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.5),
    borderRadius: '4px',
    bgcolor: 'transparent',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'inherit',
    color: 'primary.main',
    cursor: 'pointer',
};

export function LiveTradesSidebarView({
    markets,
    liveProbs,
    connected,
    loadMoreTrades,
    loadingMore,
    hasMore,
    error,
    highlightedAccounts,
    selectedTraderTradesLoading,
    tradesByAccount,
    accountColorMap,
    outcomeColorMap,
    onSelectMarket,
    getTraderHref,
    getMarketHref,
    onToggleAccount,
    disableTraderTooltip,
    signals,
    hoveredTradeKey,
    onTradeHover,
    timeRange,
    loadedForWindow = 0,
    tradeSelection,
}: LiveTradesSidebarViewProps) {
    const theme = useTheme();
    const [showScrollTop, setShowScrollTop] = useState(false);
    const virtuosoRef = useRef<any>(null);

    // Sort / volume-filter controls, collapsed behind the header's tune button.
    const [controlsOpen, setControlsOpen] = useState(false);

    // The trade set itself is owned above this component, so the chart overlay
    // and this list cannot disagree about which trades the window holds.
    const {
        filter,
        setFilter,
        setSortField,
        setSortAsc,
        setMinVolume,
        highlightedAddrs,
        liveSidebarTrades,
        windowTrades,
        accountFilteredTrades,
        filteredTrades,
        windowVolume,
        windowIsLive,
        canRankByValue,
        effectiveSortField,
        effectiveSortAsc,
        effectiveMinVolume,
        controlsActive,
        activeControlCount,
    } = tradeSelection;

    // Tooltip: track which trader address is being hovered
    const [hoveredAddress, setHoveredAddress] = useState<string | null>(null);
    const traderProfile = useTraderProfileCache(
        disableTraderTooltip ? undefined : hoveredAddress ?? undefined
    );

    const hasHighlighted = highlightedAccounts && highlightedAccounts.size > 0;

    // Build market label lookup — prefer yes_outcome (short) over full question
    const isMultiMarket = markets.length > 1;
    const marketLabels = useMemo(() => {
        const map = new Map<string, string>();
        for (const m of markets) {
            map.set(
                m.condition_id,
                isMultiMarket && m.yes_outcome ? m.yes_outcome : m.question
            );
        }
        return map;
    }, [markets, isMultiMarket]);

    // conditionId → outcome name pair, for naming trade sides
    const marketOutcomes = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const m of markets) {
            if (m.outcomes) map.set(m.condition_id, m.outcomes);
        }
        return map;
    }, [markets]);

    // Build market probability lookup for gain fallback
    const marketProbs = useMemo(() => {
        const map = new Map<string, number>();
        for (const m of markets) {
            if (m.current_yes_prob != null)
                map.set(m.condition_id, m.current_yes_prob);
        }
        return map;
    }, [markets]);

    const { flashIds, hasFlashed } = useNewTradeFlash(liveSidebarTrades);
    const traderTags = useTraderTags(highlightedAddrs);
    const getMatchedSignals = useTradeSignalMatch(signals);

    const showingLive = connected && windowIsLive;

    const {
        cap: drainCap,
        draining,
        drainCapped,
        drainFailed,
        retry: retryDrain,
    } = useTradeDrain({
        active: controlsActive,
        hasMore,
        loading: loadingMore,
        error,
        loadedForWindow,
        loadMore: loadMoreTrades,
    });

    const resetControls = useCallback(() => {
        setSortField('time');
        setSortAsc(false);
        setMinVolume(0);
    }, [setSortField, setSortAsc, setMinVolume]);

    // Which stage emptied the list — only the threshold is undoable by Reset.
    const excludedByControls =
        filteredTrades.length === 0 &&
        accountFilteredTrades.length > 0 &&
        effectiveMinVolume > 0;
    const excludedByAccounts =
        filteredTrades.length === 0 &&
        accountFilteredTrades.length === 0 &&
        windowTrades.length > 0;

    // Compute avg buy price per highlighted trader (for gain annotation)
    const avgBuyPrices = useMemo(() => {
        if (!highlightedAccounts || !tradesByAccount)
            return new Map<string, number>();
        const result = new Map<string, number>();
        for (const [accountId, trades] of tradesByAccount) {
            if (!highlightedAccounts.has(accountId)) continue;
            const buys = trades.filter(t => t.side === 'buy');
            if (buys.length === 0) continue;
            const totalCost = buys.reduce(
                (sum, t) => sum + t.price * t.amount,
                0
            );
            const totalVolume = buys.reduce((sum, t) => sum + t.amount, 0);
            const avgPrice = totalVolume > 0 ? totalCost / totalVolume : 0;
            const addr = accountId.replace(/^polymarket:/, '');
            result.set(addr, avgPrice);
        }
        return result;
    }, [highlightedAccounts, tradesByAccount]);

    // Compute gain for a trade (if trader is highlighted)
    const getGain = useCallback(
        (trade: MarketTrade): number | undefined => {
            if (!highlightedAddrs.has(trade.account)) return undefined;

            const avgBuy = avgBuyPrices.get(trade.account);
            if (avgBuy === undefined) return undefined;

            if (trade.side === 'buy') {
                const currentProb =
                    liveProbs?.get(trade.marketId) ??
                    marketProbs.get(trade.marketId) ??
                    trade.price;
                return Math.round((currentProb - trade.price) * 100);
            } else {
                return Math.round((trade.price - avgBuy) * 100);
            }
        },
        [highlightedAddrs, avgBuyPrices, liveProbs, marketProbs]
    );

    // Get trader color for emphasis
    const getTraderColor = useCallback(
        (trade: MarketTrade): string | undefined => {
            if (!accountColorMap) return undefined;
            const prefixed = `polymarket:${trade.account}`;
            return (
                accountColorMap.get(prefixed) ??
                accountColorMap.get(trade.account)
            );
        },
        [accountColorMap]
    );

    // Is this trade greyed out? (All mode with highlighted accounts, but this trade isn't one of them)
    const isGreyed = useCallback(
        (trade: MarketTrade): boolean => {
            if (filter !== 'all' || !hasHighlighted) return false;
            return !highlightedAddrs.has(trade.account);
        },
        [filter, hasHighlighted, highlightedAddrs]
    );

    const handleEndReached = useCallback(() => {
        // The drain owns loading while controls are on; rows no longer arrive in
        // list order, and scroll-paging would walk silently past the fence.
        if (controlsActive) return;
        if (hasMore && !loadingMore) loadMoreTrades();
    }, [controlsActive, hasMore, loadingMore, loadMoreTrades]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Header — always pinned on top */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: showingLive
                                ? 'success.main'
                                : 'text.disabled',
                            ...(showingLive ? DOT_BREATHE_SX : {}),
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {showingLive ? 'Live Trades' : 'Trade History'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {hasHighlighted && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={filter}
                            onChange={(_, v) => v && setFilter(v)}
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '10px',
                                    fontWeight: 600,
                                },
                            }}
                        >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="accounts">
                                Accounts
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Tooltip
                        title={
                            controlsOpen
                                ? 'Hide sort & filter'
                                : 'Sort & filter'
                        }
                        placement="left"
                    >
                        <Box
                            component="button"
                            type="button"
                            aria-label={
                                activeControlCount > 0
                                    ? `Sort and filter trades, ${activeControlCount} active`
                                    : 'Sort and filter trades'
                            }
                            aria-expanded={controlsOpen}
                            onClick={() => setControlsOpen(o => !o)}
                            sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 26,
                                height: 24,
                                p: 0,
                                border: 1,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                borderColor:
                                    controlsOpen || controlsActive
                                        ? alpha(theme.palette.primary.main, 0.5)
                                        : 'divider',
                                bgcolor:
                                    controlsOpen || controlsActive
                                        ? alpha(
                                            theme.palette.primary.main,
                                            0.14
                                        )
                                        : 'transparent',
                                color:
                                    controlsOpen || controlsActive
                                        ? 'primary.main'
                                        : 'text.secondary',
                                '&:hover': { borderColor: 'text.secondary' },
                            }}
                        >
                            <TuneIcon sx={{ fontSize: 14 }} />
                            {activeControlCount > 0 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        right: -5,
                                        minWidth: 12,
                                        height: 12,
                                        px: '2px',
                                        borderRadius: '6px',
                                        bgcolor: 'primary.main',
                                        color: 'background.paper',
                                        fontSize: '8px',
                                        fontWeight: 700,
                                        fontFamily: 'monospace',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {activeControlCount}
                                </Box>
                            )}
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            {/* Chart window — present only while the chart is zoomed */}
            {timeRange && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        px: 1,
                        py: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        flexShrink: 0,
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                color: 'primary.main',
                                flexGrow: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatRange(timeRange)}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            color: 'text.secondary',
                        }}
                    >
                        {windowTrades.length.toLocaleString()}
                        {hasMore ? '+' : ''} trades ·{' '}
                        {formatVolume(windowVolume)}
                        {hasMore ? '+' : ''}
                    </Typography>
                </Box>
            )}

            {/* Sort & volume filter */}
            <Collapse in={controlsOpen} timeout={140} unmountOnExit>
                <TradeControlsPanel
                    sortField={effectiveSortField}
                    sortAsc={effectiveSortAsc}
                    minVolume={effectiveMinVolume}
                    canRankByValue={canRankByValue}
                    shownCount={filteredTrades.length}
                    windowCount={windowTrades.length}
                    hasMore={hasMore}
                    resetDisabled={!controlsActive}
                    onSortFieldChange={setSortField}
                    onToggleSortDirection={() => setSortAsc(a => !a)}
                    onMinVolumeChange={setMinVolume}
                    onReset={resetControls}
                />
            </Collapse>

            {/* Outside the Collapse: closing the panel must not hide the fence. */}
            {draining && (
                <Box
                    role="status"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                >
                    <CircularProgress size={11} thickness={5} />
                    <Typography sx={CONTROL_HINT_SX}>
                        Loading the rest of this window to rank it —{' '}
                        {loadedForWindow.toLocaleString()} so far.
                    </Typography>
                </Box>
            )}
            {drainFailed && (
                <Box
                    role="status"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.error.main, 0.12),
                        flexShrink: 0,
                    }}
                >
                    <WarningAmberIcon
                        sx={{ fontSize: 13, color: 'error.main' }}
                    />
                    <Typography
                        sx={{
                            ...CONTROL_HINT_SX,
                            color: 'error.main',
                            flexGrow: 1,
                        }}
                    >
                        Couldn't load the rest of this window — ranking{' '}
                        {loadedForWindow.toLocaleString()} trades so far.
                    </Typography>
                    <Box
                        component="button"
                        type="button"
                        onClick={retryDrain}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 20,
                            px: 0.75,
                            flexShrink: 0,
                            border: 1,
                            borderColor: alpha(theme.palette.error.main, 0.5),
                            borderRadius: '4px',
                            bgcolor: 'transparent',
                            fontSize: '10px',
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            color: 'error.main',
                            cursor: 'pointer',
                        }}
                    >
                        Retry
                    </Box>
                </Box>
            )}
            {drainCapped && (
                <Box
                    role="status"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        flexShrink: 0,
                    }}
                >
                    <WarningAmberIcon
                        sx={{ fontSize: 13, color: 'warning.main' }}
                    />
                    <Typography
                        sx={{ ...CONTROL_HINT_SX, color: 'warning.main' }}
                    >
                        Ranking the newest {drainCap.toLocaleString()} trades
                        only — this window holds more. Zoom in for a complete
                        ranking.
                    </Typography>
                </Box>
            )}

            {/* Highlighted accounts bar */}
            {hasHighlighted && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderBottom: 1,
                        flexShrink: 0,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                        borderColor: 'divider',
                    }}
                >
                    {[...highlightedAddrs].map(addr => {
                        const prefixed = `polymarket:${addr}`;
                        const color =
                            accountColorMap?.get(prefixed) ??
                            accountColorMap?.get(addr) ??
                            theme.palette.text.secondary;
                        const short =
                            addr.length > 10
                                ? `${addr.slice(0, 6)}…${addr.slice(-4)}`
                                : addr;
                        const tags = traderTags.get(addr) ?? [];
                        return (
                            <Box
                                key={addr}
                                onClick={() => onToggleAccount?.(addr)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.15,
                                    borderRadius: '3px',
                                    bgcolor: alpha(color, 0.1),
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: alpha(color, 0.2) },
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '9px',
                                        fontFamily: 'monospace',
                                        color,
                                    }}
                                >
                                    {short}
                                </Typography>
                                {tags.map(tag => (
                                    <Badge
                                        key={tag}
                                        color={
                                            TAG_COLORS[tag] ??
                                            theme.palette.text.secondary
                                        }
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                                <Typography
                                    component="span"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onToggleAccount?.(addr);
                                    }}
                                    sx={{
                                        fontSize: '9px',
                                        color: 'text.disabled',
                                        cursor: 'pointer',
                                        ml: 0.25,
                                        '&:hover': { color: 'error.main' },
                                    }}
                                >
                                    ×
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Trade list */}
            {/* `draining` keeps the spinner up between pages: without it the
                empty state and the spinner alternate once per page for as long
                as the drain runs. */}
            {filteredTrades.length === 0 &&
                (loadingMore || draining || selectedTraderTradesLoading) ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    <CircularProgress size={16} />
                </Box>
            ) : filteredTrades.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: showingLive
                                ? 'success.main'
                                : 'text.disabled',
                            ...(showingLive ? DOT_BREATHE_SX : {}),
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.disabled',
                            textAlign: 'center',
                            px: 2,
                        }}
                    >
                        {error
                            ? 'Failed to load trades'
                            : excludedByControls
                                ? 'No trades match these filters'
                                : excludedByAccounts
                                    ? timeRange
                                        ? 'No trades from the selected accounts in this window'
                                        : 'No trades from the selected accounts'
                                    : timeRange
                                        ? 'No trades in this window'
                                        : 'No trades yet'}
                    </Typography>
                    {!error && excludedByControls && (
                        <Box
                            component="button"
                            type="button"
                            onClick={resetControls}
                            sx={EMPTY_ACTION_SX}
                        >
                            Reset filters
                        </Box>
                    )}
                    {!error && timeRange && windowTrades.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                textAlign: 'center',
                                px: 2,
                            }}
                        >
                            Zoom out on the chart to widen it.
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{ position: 'relative', flex: 1 }}>
                    <Virtuoso
                        ref={virtuosoRef}
                        style={{
                            height: '100%',
                            overscrollBehavior: 'contain',
                        }}
                        data={filteredTrades}
                        endReached={handleEndReached}
                        overscan={200}
                        atTopStateChange={(atTop: any) =>
                            setShowScrollTop(!atTop)
                        }
                        className="hide-scrollbar"
                        itemContent={(index: any, trade: MarketTrade) => (
                            <TradeRow
                                key={trade.id}
                                trade={trade}
                                marketQuestion={
                                    marketLabels.get(trade.marketId) ??
                                    trade.marketId.slice(0, 16) + '…'
                                }
                                outcomes={marketOutcomes.get(trade.marketId)}
                                gain={getGain(trade)}
                                traderColor={getTraderColor(trade)}
                                outcomeColor={outcomeColorMap?.get(
                                    trade.marketId
                                )}
                                isNew={
                                    flashIds.has(trade.id) &&
                                    !hasFlashed(trade.id)
                                }
                                greyed={isGreyed(trade)}
                                tags={traderTags.get(trade.account)}
                                onClickMarket={
                                    onSelectMarket
                                        ? () => onSelectMarket(trade.marketId)
                                        : undefined
                                }
                                marketHref={
                                    getMarketHref
                                        ? getMarketHref(
                                            trade.marketId,
                                            marketLabels.has(trade.marketId)
                                        )
                                        : undefined
                                }
                                traderHref={
                                    getTraderHref
                                        ? getTraderHref(trade.account)
                                        : undefined
                                }
                                onToggleAccount={
                                    onToggleAccount
                                        ? () => onToggleAccount(trade.account)
                                        : undefined
                                }
                                tooltipContent={
                                    !disableTraderTooltip &&
                                        trade.account === hoveredAddress ? (
                                        <TraderStatsTooltip
                                            address={trade.account}
                                            profile={traderProfile.data}
                                            loading={traderProfile.loading}
                                            refreshing={
                                                traderProfile.refreshing
                                            }
                                            error={traderProfile.error}
                                            traderHref={
                                                getTraderHref
                                                    ? getTraderHref(
                                                        trade.account
                                                    )
                                                    : undefined
                                            }
                                        />
                                    ) : undefined
                                }
                                onHoverAddress={
                                    disableTraderTooltip
                                        ? undefined
                                        : () => setHoveredAddress(trade.account)
                                }
                                onLeaveAddress={
                                    disableTraderTooltip
                                        ? undefined
                                        : () => setHoveredAddress(null)
                                }
                                matchedSignals={getMatchedSignals(trade)}
                                isTradeHovered={
                                    hoveredTradeKey != null &&
                                    hoveredTradeKey === tradeKey(trade)
                                }
                                onTradeHover={onTradeHover}
                            />
                        )}
                        components={{
                            Footer: () =>
                                loadingMore ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            py: 2,
                                        }}
                                    >
                                        <CircularProgress size={16} />
                                    </Box>
                                ) : null,
                        }}
                    />
                    {showScrollTop && (
                        <Fab
                            size="small"
                            onClick={() =>
                                virtuosoRef.current?.scrollToIndex({
                                    index: 0,
                                    behavior: 'smooth',
                                })
                            }
                            sx={{
                                position: 'absolute',
                                top: 8,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 28,
                                height: 28,
                                minHeight: 'unset',
                                bgcolor: 'action.hover',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'action.selected' },
                            }}
                        >
                            <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                        </Fab>
                    )}
                    <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
                </Box>
            )}
        </Box>
    );
}

/**
 * Self-contained sidebar: owns its own trade selection.
 *
 * EventView renders LiveTradesSidebarView directly instead, because it has to
 * hand the same selection to the chart overlay. Every other caller wants this
 * one and should not have to know the selection exists.
 */
export function LiveTradesSidebar(props: LiveTradesSidebarProps) {
    const tradeSelection = useEventTradeSelection({
        liveTrades: props.liveTrades,
        historicalTrades: props.historicalTrades,
        selectedTraderTrades: props.selectedTraderTrades,
        timeRange: props.timeRange,
        highlightedAccounts: props.highlightedAccounts,
    });
    return <LiveTradesSidebarView {...props} tradeSelection={tradeSelection} />;
}
